import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { AttachmentPreview } from './AttachmentPreview';
import type { OptimisticMessage } from '@/hooks/useMessages';

interface MessageBubbleProps {
  message: OptimisticMessage;
  onRetry?: () => void;
}

export function MessageBubble({ message, onRetry }: MessageBubbleProps) {
  const isOperator = message.sender_type === 'operator';
  const isSystem = message.sender_type === 'system';
  const isSending = message._status === 'sending';
  const isFailed = message._status === 'failed';

  const time = format(new Date(message.created_at), 'HH:mm');

  if (isSystem) {
    return (
      <div className="flex justify-center my-2">
        <div className="bg-gray-100 text-gray-500 text-xs px-3 py-1 rounded-full">
          {message.sanitized_text}
        </div>
      </div>
    );
  }

  return (
    <div className={cn('flex mb-1.5 group', isOperator ? 'justify-end' : 'justify-start')}>
      {/* Avatar for customer */}
      {!isOperator && (
        <div className="w-6 h-6 rounded-full bg-gray-300 flex-shrink-0 mr-2 flex items-center justify-center">
          <span className="text-xs text-gray-600 font-medium">C</span>
        </div>
      )}

      <div className="flex flex-col items-end max-w-[75%]">
        <div className="flex items-end gap-1.5">
          {/* Time - shown on hover for operator messages */}
          {isOperator && !isSending && !isFailed && (
            <span className="text-[10px] text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity mb-0.5">
              {time}
            </span>
          )}

          <div
            className={cn(
              'rounded-2xl px-3 py-1.5 text-sm',
              isOperator
                ? isFailed
                  ? 'bg-red-100 text-red-800'
                  : isSending
                    ? 'bg-blue-400 text-white'
                    : 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-900'
            )}
          >
            <p className="whitespace-pre-wrap break-words leading-snug">{message.sanitized_text}</p>

            {/* Attachments */}
            {message.attachments && message.attachments.length > 0 && (
              <div className="mt-1">
                {message.attachments.map((attachment) => (
                  <AttachmentPreview
                    key={attachment.id}
                    attachment={attachment}
                    isOperator={isOperator}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Time - shown on hover for customer messages */}
          {!isOperator && (
            <span className="text-[10px] text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity mb-0.5">
              {time}
            </span>
          )}
        </div>

        {/* Status indicators for operator messages */}
        {isOperator && (isSending || isFailed) && (
          <div className="mt-0.5 mr-1 flex items-center gap-1">
            {isSending && (
              <span className="text-[10px] text-gray-400 italic">Sending...</span>
            )}
            {isFailed && (
              <>
                <span className="text-[10px] text-red-500">Failed</span>
                {onRetry && (
                  <button
                    onClick={onRetry}
                    className="text-[10px] text-red-600 hover:text-red-700 underline font-medium"
                  >
                    Retry
                  </button>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* Avatar for operator */}
      {isOperator && (
        <div
          className={cn(
            'w-6 h-6 rounded-full flex-shrink-0 ml-2 flex items-center justify-center',
            isFailed ? 'bg-red-400' : 'bg-blue-600'
          )}
        >
          <span className="text-xs text-white font-medium">O</span>
        </div>
      )}
    </div>
  );
}
